import { HydrateAction, hydrateType } from '@spa/lib/store/HydrateAction';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { UserState } from './UserState';
import { setAuthState } from '@spa/lib/store/auth/authSlice';

export interface SetUserEmailPayload {
  readonly email: string | null;
}

export interface SetIsInEmployeeCustomerGroupPayload {
  readonly authenticatedUserId: string;
  readonly isInEmployeeCustomerGroup: boolean | null;
}

const initialState: UserState = {
  employeeGroupCheckedForUserId: null,
  email: null,
  isEmployeeLinked: false,
  isInEmployeeCustomerGroup: null
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserEmail: (state, action: PayloadAction<SetUserEmailPayload>) => {
      state.email = action.payload.email;
    },
    setIsEmployeeLinked(state, { payload }: PayloadAction<boolean>) {
      state.isEmployeeLinked = payload ?? false;
    },
    setIsInEmployeeCustomerGroup(
      state,
      { payload }: PayloadAction<SetIsInEmployeeCustomerGroupPayload>
    ) {
      state.employeeGroupCheckedForUserId = payload.authenticatedUserId;
      state.isInEmployeeCustomerGroup = payload.isInEmployeeCustomerGroup;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateType, (state, { payload }: HydrateAction): void => {
      const authenticatedUserId = payload.auth?.authenticatedUserId;
      const employeeGroupCheckedForUserId = payload.user?.employeeGroupCheckedForUserId;
      const isInEmployeeCustomerGroup = payload.user?.isInEmployeeCustomerGroup;

      if (
        authenticatedUserId === null ||
        (typeof authenticatedUserId === 'string' &&
          state.employeeGroupCheckedForUserId !== null &&
          state.employeeGroupCheckedForUserId !== authenticatedUserId)
      ) {
        state.employeeGroupCheckedForUserId = null;
        state.isInEmployeeCustomerGroup = null;
      }

      if (
        typeof employeeGroupCheckedForUserId === 'string' &&
        employeeGroupCheckedForUserId === authenticatedUserId
      ) {
        state.employeeGroupCheckedForUserId = employeeGroupCheckedForUserId;
        state.isInEmployeeCustomerGroup =
          typeof isInEmployeeCustomerGroup === 'boolean' ? isInEmployeeCustomerGroup : null;
      }
    });

    builder.addCase(setAuthState, (state, { payload }): void => {
      const { authenticatedUserId } = payload;

      if (
        authenticatedUserId === null ||
        (state.employeeGroupCheckedForUserId !== null &&
          state.employeeGroupCheckedForUserId !== authenticatedUserId)
      ) {
        state.employeeGroupCheckedForUserId = null;
        state.isInEmployeeCustomerGroup = null;
      }
    });
  }
});

export const { setUserEmail, setIsEmployeeLinked, setIsInEmployeeCustomerGroup } =
  userSlice.actions;

export default userSlice.reducer;

type SliceActions<T> = {
  [K in keyof T]: T[K] extends (...args: never[]) => infer A ? A : never;
}[keyof T];

type ActionTypes = SliceActions<typeof userSlice.actions>;

export const setEmailType = 'user/setEmail';
export type SetEmailAction = Extract<
  ActionTypes,
  PayloadAction<SetUserEmailPayload, typeof setEmailType>
>;
